/**
 * ActivityListItem — Sprint 9 / neue Backend-API
 *
 * Rendert einen gruppierten ActivityGroup-Eintrag.
 * - human_label als Titel (kommt vom BE)
 * - Hotspot-Darstellung: 1 Hotspot → Name; >1 → "ErsterName +N" oder "N Hotspots"
 * - token + rewards_total > 0 → Betrag mit Token-Badge
 * - occurred_at → relative Zeit
 * - origin (PoC-Details) → optionale Detailzeile
 * - era 'pre' → dezentes pre-HIP-138-Badge
 * - Icon/Farbe aus actionTypeConfig
 */
import React from 'react'
import { useTranslation } from 'react-i18next'
import {
  Clock,
  ArrowUpRight,
  Sparkles,
  Radio,
  Eye,
  Crosshair,
  Send,
  Activity,
  Gauge,
  Database,
  Wifi,
  PlusCircle,
  ArrowRightLeft,
  MapPin,
  Signal,
  Coins,
  Zap,
} from 'lucide-react'
import { getActionTypeConfig } from './constants/actionTypeConfig'
import { formatActivityDate } from './activityUtils'
import { ActivityDetailLine } from './ActivityDetailLine'
import type { ActivityGroup } from './api/activityApi'

// --- Icon-Map ---

const ICON_MAP: Record<string, React.ComponentType<{ size?: number; style?: React.CSSProperties }>> = {
  Clock,
  Sparkles,
  Radio,
  Eye,
  Crosshair,
  Send,
  Activity,
  Gauge,
  Database,
  Wifi,
  PlusCircle,
  ArrowRightLeft,
  MapPin,
  Signal,
  Coins,
  Zap,
  ArrowUpRight,
}

// --- ActionIcon ---

function ActionIcon({ iconName, color }: { iconName: string; color: string }) {
  const Icon = ICON_MAP[iconName] ?? Clock
  return (
    <span
      className="activity-icon"
      style={{ color, background: 'transparent' }}
      aria-hidden="true"
    >
      <Icon size={14} />
    </span>
  )
}

// --- Token-Mark ---

function TokenMark({ token }: { token: string }) {
  const cls =
    token === 'hnt' ? 't-hnt'
    : token === 'iot' ? 't-iot'
    : token === 'mobile' ? 't-mobile'
    : token === 'dc' ? 't-dc'
    : 't-sol'
  return <span className={`token-mark ${cls}`}>{token.toUpperCase()[0]}</span>
}

// --- Hotspot-Display ---

function HotspotDisplay({ hotspots, hotspot_count }: { hotspots: ActivityGroup['hotspots']; hotspot_count: number }) {
  if (hotspot_count === 0) return null

  if (hotspot_count === 1) {
    const name = hotspots[0]?.name
    return (
      <span className="hotspot-name">
        {name ?? `Hotspot #${hotspots[0]?.id ?? '?'}`}
      </span>
    )
  }

  // Mehrere Hotspots
  const firstName = hotspots[0]?.name ?? `Hotspot #${hotspots[0]?.id ?? '?'}`
  const rest = hotspot_count - 1
  return (
    <span className="hotspot-name">
      {firstName}{' '}
      <span className="hotspot-more">+{rest}</span>
    </span>
  )
}

// --- PoC-Origin-Details ---

function OriginDetail({ origin }: { origin: ActivityGroup['origin'] }) {
  const { t } = useTranslation('activity')
  if (!origin) return null

  const parts: string[] = []
  if (origin.city) parts.push(origin.city)
  if (origin.valid_witness_count != null) {
    parts.push(t('item.witnesses', { count: origin.valid_witness_count }))
  }
  if (origin.transmit_scale != null) {
    parts.push(t('item.scale', { value: origin.transmit_scale.toFixed(2) }))
  }
  if (origin.distance != null) {
    const km = (origin.distance / 1000).toFixed(1)
    parts.push(`${km} km`)
  }

  if (parts.length === 0) return null

  return (
    <div className="origin-detail muted" style={{ fontSize: '11px', marginTop: '2px' }}>
      {parts.join(' · ')}
    </div>
  )
}

// --- era-Badge ---

function EraBadge({ era }: { era: 'pre' | 'post' }) {
  const { t } = useTranslation('activity')
  if (era !== 'pre') return null
  return (
    <span
      className="pill pill-muted era-badge"
      style={{ fontSize: '10px', opacity: 0.7, padding: '1px 5px' }}
      title={t('item.eraBadgeTitle')}
    >
      pre-HIP-138
    </span>
  )
}

// --- Pill-Variante nach Kategorie ---

function getPillVariant(kind: string): string {
  const config = getActionTypeConfig(kind)
  switch (config.category) {
    case 'rewards':
      return 'pill-success'
    case 'poc':
      return 'pill-primary'
    case 'mobile':
      return 'pill-accent'
    case 'data':
      return 'pill-info'
    case 'status':
    default:
      return 'pill-muted'
  }
}

// --- Bekannte BE-Tippfehler defensiv korrigieren ---

/**
 * `item.human_label` kommt unverändert vom Backend (state_channel_close_v1 →
 * "Data Transfered", verifiziert per curl gegen GET /api/v2/activities,
 * demo_test@heliumtracker.io, :3927 — unabhängig von Accept-Language, da
 * human_label nicht lokalisiert wird). Der Tippfehler lebt NICHT in den
 * FE-i18n-Katalogen (src/i18n/locales/{en,de}/activity.json sind bereits
 * typofrei) und diese Story darf keine Backend-/Seed-Dateien anfassen
 * (F-SW7-15 Boundary). Analog zum bereits etablierten Muster in
 * features/reports/HotspotRewardTable.tsx (defensives UI-Abfangen fehlerhafter
 * BE-Strings) wird der bekannte Tippfehler hier defensiv im UI korrigiert.
 * Die Korrektur der Backend-Quelle selbst ist an das Team zu eskalieren.
 */
const KNOWN_HUMAN_LABEL_TYPOS: Record<string, string> = {
  'Data Transfered': 'Data Transferred',
}

function fixKnownHumanLabelTypo(label: string): string {
  return KNOWN_HUMAN_LABEL_TYPOS[label] ?? label
}

// --- Haupt-Komponente ---

type ActivityListItemProps = {
  item: ActivityGroup
  index: number
  /**
   * Nutzer-Einstellung account.use_metric (Metric/Imperial). Wird von der Seite
   * durchgereicht statt hier per Hook geholt, damit die Komponente ohne
   * QueryClient rendert (und in Tests isoliert bleibt). Default: metrisch.
   */
  useMetric?: boolean
}

export function ActivityListItem({ item, index, useMetric = true }: ActivityListItemProps) {
  const config = getActionTypeConfig(item.kind)
  const { display } = formatActivityDate(item.occurred_at)
  const pillVariant = getPillVariant(item.kind)

  const showReward = item.token != null && item.rewards_total > 0

  return (
    <div
      className="list-row activity-item"
      role="listitem"
      data-kind={item.kind}
      style={{
        gridTemplateColumns: '32px minmax(180px, 1.2fr) 1fr 220px auto auto',
        gap: '14px',
      }}
    >
      {/* Index */}
      <div
        style={{
          color: 'var(--text-subtle)',
          fontFamily: 'var(--font-mono)',
          fontSize: '11px',
          textAlign: 'right',
        }}
      >
        {(index + 1).toString().padStart(3, '0')}
      </div>

      {/* Identity Column */}
      <div className="identity">
        <div
          className="identity-img"
          style={{
            width: '32px',
            height: '32px',
            borderRadius: '9px',
            fontSize: '12px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: config.softColor,
          }}
        >
          <ActionIcon iconName={config.icon} color={config.color} />
        </div>
        <div className="identity-meta">
          <div className="identity-title" style={{ fontSize: '13px' }}>
            {fixKnownHumanLabelTypo(item.human_label)}
            {item.era === 'pre' && ' '}
            <EraBadge era={item.era} />
          </div>
          <div className="identity-sub">
            <span>{display}</span>
            {item.activity_count > 1 && (
              <span className="activity-count muted" style={{ marginLeft: '6px', fontSize: '11px' }}>
                {item.activity_count}x
              </span>
            )}
          </div>
          <OriginDetail origin={item.origin} />
          {/* R5/FD: v1-Detailzeilen, bewusst IMMER sichtbar (kein Aufklappen). */}
          <ActivityDetailLine kind={item.kind} details={item.details} useMetric={useMetric} />
        </div>
      </div>

      {/* Hotspot Column */}
      <div className="muted" style={{ fontSize: '13px' }}>
        <HotspotDisplay hotspots={item.hotspots} hotspot_count={item.hotspot_count} />
      </div>

      {/* Pill Badge */}
      <div>
        <span className={`pill ${pillVariant}`}>{item.kind.replace(/_/g, ' ')}</span>
      </div>

      {/* Reward Column */}
      <div
        style={{
          textAlign: 'right',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          gap: '8px',
        }}
      >
        {showReward && (
          <React.Fragment>
            <TokenMark token={item.token!} />
            <div>
              <div
                className="num-sm"
                style={{ fontWeight: '600', color: 'var(--success)' }}
              >
                +{item.rewards_total.toFixed(2)}
              </div>
            </div>
          </React.Fragment>
        )}
      </div>

      {/* Arrow */}
      <div style={{ color: 'var(--text-subtle)' }}>
        <ArrowUpRight size={14} />
      </div>
    </div>
  )
}
